import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import api, { IMAGE_BASE_URL } from "../api/Api.js";
import printImage from "../assets/images/print.png";
import newReservationImage from "../assets/images/newreservation.png";
import {
  useNavigate,
  useLocation,
} from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";

import "../assets/css/style.css";

import { useLanguage } from "../context/LanguageContext";

// ============================================================
// COMPONENT
// ============================================================

function ConfirmBooking() {
  const navigate = useNavigate();
  const location = useLocation();

  // ==========================================================
  // LANGUAGE
  // Same architecture as BookACarThree
  // ==========================================================

  const {
    language,
    t,
  } = useLanguage();

  // ==========================================================
  // LANGUAGE ID
  // ==========================================================

  const languageId = Number(
    language?.lang_id ||
    language?.language_id ||
    language?.id ||
    language ||
    1
  );

  // ==========================================================
  // TRANSLATION HELPERS
  // Same architecture as BookACarThree
  // ==========================================================

  const bt = (key, fallback = "") => {
    return t(
      `bookingengine.${key}`,
      fallback
    );
  };

  const pt = (key, fallback = "") => {
    return t(
      `personaldtls.${key}`,
      fallback
    );
  };

  const rt = (key, fallback = "") => {
    return t(
      `ratechart.${key}`,
      fallback
    );
  };

  const et = (key, fallback = "") => {
    return t(
      `extracharges.${key}`,
      fallback
    );
  };

  // ==========================================================
  // STATE
  // ==========================================================

  const [booking, setBooking] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  // ==========================================================
  // LOCATIONS
  // IMPORTANT:
  // No hardcoded LOCATIONS object.
  // Everything comes from API/database.
  // ==========================================================

  const [locations, setLocations] = useState([]);

  const [locationsLoading, setLocationsLoading] =
    useState(false);

  // ==========================================================
  // EXTRAS
  // IMPORTANT:
  // No hardcoded PAID_EXTRAS.
  // Everything comes from API/database.
  // ==========================================================

  const [freeExtras, setFreeExtras] = useState([]);

  const [paidExtras, setPaidExtras] = useState([]);

  const [freeInsurance, setFreeInsurance] = useState([]);

  const [paidInsurance, setPaidInsurance] = useState([]);

  const [extrasLoading, setExtrasLoading] =
    useState(false);

  // ==========================================================
  // RESOLVE BOOKING ID
  // ==========================================================

  const resolveBookingId = () => {
    // --------------------------------------------------------
    // 1. Navigation state
    // --------------------------------------------------------

    if (location.state?.bookingid) {
      return location.state.bookingid;
    }

    // --------------------------------------------------------
    // 2. Session storage
    // --------------------------------------------------------

    try {
      const stored =
        sessionStorage.getItem("finalBooking");

      if (stored) {
        const parsed =
          JSON.parse(stored);

        if (parsed?.bookingid) {
          return parsed.bookingid;
        }
      }
    } catch (storageError) {
      console.warn(
        "Could not parse finalBooking from sessionStorage:",
        storageError
      );
    }

    // --------------------------------------------------------
    // 3. URL
    // --------------------------------------------------------

    const params =
      new URLSearchParams(location.search);

    const fromQuery =
      params.get("bookingid");

    return fromQuery || null;
  };

  // ==========================================================
  // LOAD LOCATIONS
  // Same API architecture used by BookACar
  // ==========================================================

  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLocationsLoading(true);

        console.log(
          "================================="
        );

        console.log(
          "LOADING CONFIRMATION LOCATIONS"
        );

        console.log(
          "Language ID:",
          languageId
        );

        console.log(
          "================================="
        );

        const response = await api.get(
          "/locations",
          {
            params: {
              city_id: 1,
              lang_id: languageId,
            },
          }
        );

        console.log(
          "LOCATIONS RESPONSE:",
          response
        );

        const result =
          response.data;

        if (
          !result ||
          !result.success
        ) {
          setLocations([]);
          return;
        }

        const locationData =
          Array.isArray(result.data)
            ? result.data
            : [];

        setLocations(locationData);
      } catch (locationError) {
        console.error(
          "Error loading confirmation locations:",
          locationError
        );

        setLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };

    loadLocations();
  }, [languageId]);

  // ==========================================================
  // LOAD EXTRAS + INSURANCE
  // Same architecture as BookACarThree
  // ==========================================================

  useEffect(() => {
    const loadExtras = async () => {
      try {
        setExtrasLoading(true);

        console.log(
          "================================="
        );

        console.log(
          "LOADING CONFIRMATION EXTRAS"
        );

        console.log(
          "Language ID:",
          languageId
        );

        console.log(
          "================================="
        );

        const response = await api.get(
          "/extras",
          {
            params: {
              lang_id: languageId,
            },
          }
        );

        console.log(
          "CONFIRMATION EXTRAS RESPONSE:",
          response
        );

        const result =
          response.data;

        if (
          !result ||
          !result.success
        ) {
          setFreeExtras([]);
          setPaidExtras([]);
          setFreeInsurance([]);
          setPaidInsurance([]);

          return;
        }

        setFreeExtras(
          Array.isArray(result.freeExtras)
            ? result.freeExtras
            : []
        );

        setPaidExtras(
          Array.isArray(result.paidExtras)
            ? result.paidExtras
            : []
        );

        setFreeInsurance(
          Array.isArray(result.freeInsurance)
            ? result.freeInsurance
            : []
        );

        setPaidInsurance(
          Array.isArray(result.paidInsurance)
            ? result.paidInsurance
            : []
        );
      } catch (extrasError) {
        console.error(
          "Error loading confirmation extras:",
          extrasError
        );

        setFreeExtras([]);
        setPaidExtras([]);
        setFreeInsurance([]);
        setPaidInsurance([]);
      } finally {
        setExtrasLoading(false);
      }
    };

    loadExtras();
  }, [languageId]);

  // ==========================================================
  // LOCATION NAME
  // ==========================================================

  const getLocationName = (id) => {
    if (
      id === undefined ||
      id === null ||
      id === ""
    ) {
      return bt(
        "notSelected",
        "Not selected"
      );
    }

    const locationId =
      Number(id);

    const foundLocation =
      locations.find((item) => {
        return Number(
          item.location_id ??
          item.loc_id ??
          item.id ??
          item.station_id
        ) === locationId;
      });

    if (foundLocation) {
      return (
        foundLocation.name ||
        foundLocation.location_name ||
        foundLocation.loc_name ||
        foundLocation.station_name ||
        foundLocation.title ||
        String(id)
      );
    }

    return String(id);
  };

  // ==========================================================
  // TIME
  // ==========================================================

  const getTime = (
    hour,
    minute
  ) => {
    if (
      hour === undefined ||
      hour === null ||
      hour === ""
    ) {
      return "";
    }

    return `${hour}:${String(
      minute ?? "00"
    ).padStart(2, "0")}`;
  };

  // ==========================================================
  // FETCH BOOKING
  // ==========================================================

  useEffect(() => {
    const bookingid =
      resolveBookingId();

    if (!bookingid) {
      console.warn(
        "No bookingid available to fetch confirmation."
      );

      navigate("/bookacarthree");

      return;
    }

    const fetchBooking = async () => {
      setLoading(true);

      setError("");

      try {
        console.log(
          "================================="
        );

        console.log(
          "FETCHING BOOKING CONFIRMATION"
        );

        console.log(
          "Booking ID:",
          bookingid
        );

        console.log(
          "================================="
        );

        const response =
          await api.post(
            "/bookings/details",
            {
              bookingid,
            }
          );

        console.log(
          "CONFIRM BOOKING RESPONSE:",
          response
        );

        console.log(
          "CONFIRM BOOKING DATA:",
          response.data
        );

        const result =
          response.data;

        if (
          !result ||
          !result.success
        ) {
          setError(
            result?.message ||
              bt(
                "bookingConfirmationLoadError",
                "Unable to load booking confirmation."
              )
          );

          setLoading(false);

          return;
        }

        setBooking(
          mapDbBookingToViewModel(
            result.data
          )
        );
      } catch (fetchError) {
        console.error(
          "Error fetching booking confirmation:",
          fetchError
        );

        console.error(
          "ERROR RESPONSE:",
          fetchError.response
        );

        setError(
          bt(
            "bookingConfirmationServerError",
            "Server error while loading booking confirmation."
          )
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();

  }, [navigate, location.state, location.search]);

  // ==========================================================
  // MAP DB ROW -> VIEW MODEL
  // ==========================================================

  const mapDbBookingToViewModel = (
    row
  ) => {
    let sessionExtras = {
      freeExtras: [],
      paidExtras: {},
      freeInsurance: [],
      paidInsurance: {},
    };

    let sessionPaymentMethod =
      null;

    try {
      const stored =
        sessionStorage.getItem(
          "finalBooking"
        );

      if (stored) {
        const parsed =
          JSON.parse(stored);

        if (
          parsed?.bookingid ===
          row?.bookingid
        ) {
          sessionExtras = {
            freeExtras:
              parsed?.bookingData
                ?.freeExtras || [],

            paidExtras:
              parsed?.bookingData
                ?.paidExtras || {},

            freeInsurance:
              parsed?.bookingData
                ?.freeInsurance || [],

            paidInsurance:
              parsed?.bookingData
                ?.paidInsurance || {},
          };

          sessionPaymentMethod =
            parsed?.paymentMethod ||
            null;
        }
      }
    } catch (storageError) {
      console.warn(
        "Could not read extras from sessionStorage:",
        storageError
      );
    }

    const rentalDays =
      Number(
        row?.online_diffdate || 1
      );

    const pricePerDay =
      rentalDays > 0
        ? Number(
            row?.online_carprice || 0
          ) / rentalDays
        : Number(
            row?.online_carprice || 0
          );

    return {
      bookingid:
        row?.bookingid,

      fullbooking_no:
        row?.fullbooking_no,

      car: {
        carmodel_name:
          row?.carmodel_name,

        carmodel_image:
          row?.carmodel_image,

        cat_name:
          row?.cat_name,

        carmodel_passgrno:
          row?.carmodel_passgrno,

        carmodel_luggno:
          row?.carmodel_luggno,

        transmission:
          row?.transmission,

        gear:
          row?.gear,

        airco:
          row?.airco,

        air_condition:
          row?.air_condition,

        pricePerDay,
      },

      customer: {
        title:
          row?.online_title,

        firstName:
          row?.online_firstname,

        lastName:
          row?.online_lastname,

        email:
          row?.online_emailid,

        phone:
          row?.online_mobileno ||
          row?.online_telephone,

        mobile:
          row?.online_mobileno,

        address:
          row?.online_address,

        city:
          row?.online_city,

        country:
          row?.online_country,

        postcode:
          row?.online_postcode,

        remarks:
          row?.online_remarks || "",

        dropoffRemarks:
          row?.online_dropremarks ||
          row?.online_dropoffremarks ||
          "",
      },

      bookingCriteria: {
        cmbPickLocation:
          row?.online_pickstation,

        cmbDropLocation:
          row?.online_returnstation,

        pickdate:
          row?.online_pickdate,

        returndate:
          row?.online_returndate,

        cmbpickHour:
          row?.online_picktime
            ? String(
                row.online_picktime
              ).split(":")[0]
            : "",

        cmbpickmin:
          row?.online_picktime
            ? String(
                row.online_picktime
              ).split(":")[1]
            : "",

        cmbreturnHour:
          row?.online_returntime
            ? String(
                row.online_returntime
              ).split(":")[0]
            : "",

        cmbreturnmin:
          row?.online_returntime
            ? String(
                row.online_returntime
              ).split(":")[1]
            : "",
      },

      rentalDays,

      rentalPrice:
        Number(
          row?.online_carprice || 0
        ),

      pickupCharge:
        Number(
          row?.online_locpickupcharge ||
          0
        ),

      dropoffCharge:
        Number(
          row?.online_locdropoffcharge ||
          0
        ),

      oneWayCharge:
        Number(
          row?.online_onewayrentalcharges ||
          0
        ),

      rentalTotal:
        Number(
          row?.online_carprice || 0
        ) +
        Number(
          row?.online_locpickupcharge ||
          0
        ) +
        Number(
          row?.online_locdropoffcharge ||
          0
        ) +
        Number(
          row?.online_onewayrentalcharges ||
          0
        ),

      paidExtrasTotal:
        Number(
          row?.online_totaloptextras ||
          0
        ),

      paidInsuranceTotal:
        Number(
          row?.online_totalinsurance ||
          0
        ),

      finalTotal:
        Number(
          row?.online_onlineprice ||
          0
        ),

      freeExtras:
        sessionExtras.freeExtras,

      paidExtras:
        sessionExtras.paidExtras,

      freeInsurance:
        sessionExtras.freeInsurance,

      paidInsurance:
        sessionExtras.paidInsurance,

      paymentMethod:
        sessionPaymentMethod ||
        (
          Number(
            row?.payment_type
          ) === 1
            ? "paynow"
            : "paylater"
        ),
    };
  };

  // ==========================================================
  // CAR IMAGE
  // ==========================================================

  const getCarImage = (
    image
  ) => {
    if (!image) {
      return "";
    }

    let imagePath =
      String(image).trim();

    if (!imagePath) {
      return "";
    }

    if (
      imagePath.startsWith(
        "http://"
      ) ||
      imagePath.startsWith(
        "https://"
      )
    ) {
      return imagePath;
    }

    imagePath =
      imagePath.replace(
        /^\/+/,
        ""
      );

    const imageBase =
      IMAGE_BASE_URL ||
      "http://localhost:5555/uploads";

    if (
      imagePath.startsWith(
        "images/"
      )
    ) {
      return `${imageBase}/${imagePath}`;
    }

    if (
      imagePath.startsWith(
        "CarImage/"
      )
    ) {
      return `${imageBase}/images/${imagePath}`;
    }

    return `${imageBase}/images/CarImage/${imagePath}`;
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div
        className="text-center"
        style={{
          padding: "100px 20px",
        }}
      >
        <h4>
          {bt(
            "loadingBookingConfirmation",
            "Loading booking confirmation..."
          )}
        </h4>
      </div>
    );
  }

  // ==========================================================
  // ERROR
  // ==========================================================

  if (error) {
    return (
      <div
        className="text-center"
        style={{
          padding: "100px 20px",
        }}
      >
        <h4>
          {error}
        </h4>

        <button
          type="button"
          className="nextbutton1 nextbutton"
          style={{
            border: "none",
            cursor: "pointer",
            marginTop: "20px",
          }}
          onClick={() =>
            navigate("/bookacar")
          }
        >
          {bt(
            "startNewBooking",
            "Start a new booking"
          )}
        </button>
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  // ==========================================================
  // VALUES
  // ==========================================================

  const car =
    booking.car || {};

  const customer =
    booking.customer || {};

  const bookingCriteria =
    booking.bookingCriteria || {};

  const rentalDays =
    Number(
      booking.rentalDays || 1
    );

  const rentalPrice =
    Number(
      booking.rentalPrice || 0
    );

  const pickupCharge =
    Number(
      booking.pickupCharge || 0
    );

  const dropoffCharge =
    Number(
      booking.dropoffCharge || 0
    );

  const oneWayCharge =
    Number(
      booking.oneWayCharge || 0
    );

  const rentalTotal =
    Number(
      booking.rentalTotal || 0
    );

  const paidExtrasTotal =
    Number(
      booking.paidExtrasTotal || 0
    );

  const paidInsuranceTotal =
    Number(
      booking.paidInsuranceTotal || 0
    );

  const finalTotal =
    Number(
      booking.finalTotal || 0
    );

  const fullBookingNo =
    booking.fullbooking_no;

  // ==========================================================
  // CAR
  // ==========================================================

  const carName =
    car?.carmodel_name ||
    car?.name ||
    car?.carmodel ||
    bt(
      "selectedCar",
      "Selected car"
    );

  const category =
    car?.cat_name ||
    car?.category ||
    "";

  const carImage =
    getCarImage(
      car?.carmodel_image ||
      car?.image
    );

  const passengers =
    car?.carmodel_passgrno ??
    car?.passengers ??
    0;

  const luggage =
    car?.carmodel_luggno ??
    car?.luggage ??
    0;

  const transmission =
    car?.transmission ||
    car?.gear ||
    bt(
      "manual",
      "Manual"
    );

  const airCondition =
    car?.airco ||
    car?.air_condition ||
    bt(
      "airCondition",
      "Air-Co"
    );

  const pricePerDay =
    Number(
      car?.pricePerDay ??
      car?.price_perday ??
      car?.price ??
      0
    );

  // ==========================================================
  // SELECTED EXTRA IDS
  // ==========================================================

  const paidExtrasSelection =
    booking.paidExtras || {};

  const freeExtrasSelection =
    booking.freeExtras || {};

  const paidInsuranceSelection =
    booking.paidInsurance || {};

  const freeInsuranceSelection =
    booking.freeInsurance || {};

  // ==========================================================
  // HELPER TO CHECK SELECTED ID
  // ==========================================================

  const isSelectedId = (
    collection,
    id
  ) => {
    if (
      Array.isArray(collection)
    ) {
      return collection.some(
        (item) =>
          Number(item) ===
          Number(id)
      );
    }

    if (
      collection &&
      typeof collection ===
        "object"
    ) {
      return (
        Number(
          collection[id] || 0
        ) > 0
      );
    }

    return false;
  };

  // ==========================================================
  // ACTIVE PAID EXTRAS
  // FROM API
  // ==========================================================

  const activePaidExtras =
    paidExtras
      .filter((item) => {
        const id = Number(
          item.optdet_id ??
          item.optitem_id
        );

        return isSelectedId(
          paidExtrasSelection,
          id
        );
      })
      .map((item) => {
        const id = Number(
          item.optdet_id ??
          item.optitem_id
        );

        let quantity = 1;

        if (
          paidExtrasSelection &&
          typeof paidExtrasSelection ===
            "object" &&
          !Array.isArray(
            paidExtrasSelection
          )
        ) {
          quantity = Number(
            paidExtrasSelection[id] ||
            1
          );
        }

        const dayRate =
          Number(
            item.dayRate ??
            item.day_rate ??
            item.ratePerDay ??
            item.rate_per_day ??
            0
          );

        let baseTotal =
          dayRate *
          rentalDays;

        const maxRate =
          Number(
            item.maxRate || 0
          );

        if (
          maxRate > 0 &&
          baseTotal > maxRate
        ) {
          baseTotal =
            maxRate;
        }

        const total =
          baseTotal *
          quantity;

        return {
          ...item,
          id,
          quantity,
          dayRate,
          total,
        };
      });

  // ==========================================================
  // ACTIVE FREE EXTRAS
  // FROM API
  // ==========================================================

  const activeFreeExtras =
    freeExtras.filter(
      (item) => {
        const id = Number(
          item.optdet_id ??
          item.optitem_id
        );

        return isSelectedId(
          freeExtrasSelection,
          id
        );
      }
    );

  // ==========================================================
  // ACTIVE PAID INSURANCE
  // FROM API
  // ==========================================================

  const activePaidInsurance =
    paidInsurance.filter(
      (item) => {
        const id = Number(
          item.insdtl_id ??
          item.insitem_id
        );

        return isSelectedId(
          paidInsuranceSelection,
          id
        );
      }
    ).map((item) => {
      const id = Number(
        item.insdtl_id ??
        item.insitem_id
      );

      const ratePerDay =
        Number(
          item.chargePerDay ||
          0
        );

      const total =
        ratePerDay *
        rentalDays;

      return {
        ...item,
        id,
        ratePerDay,
        total,
      };
    });

  // ==========================================================
  // ACTIVE FREE INSURANCE
  // FROM API
  // ==========================================================

  const activeFreeInsurance =
    freeInsurance.filter(
      (item) => {
        const id = Number(
          item.insitem_id
        );

        return isSelectedId(
          freeInsuranceSelection,
          id
        );
      }
    );

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="p20">

      <div className="container container2 p0">

        <div
          className="col-md-12 main-content"
          style={{
            float: "left",
          }}
        >

          {/* ==================================================
              THANK YOU
          ================================================== */}

          <div className="booking-card">

            <div className="card-header">

              <h3>
                {bt(
                  "thankYouReservation",
                  "Thank you for your reservation"
                )}
              </h3>

              {fullBookingNo && (
                <p
                  style={{
                    marginTop: "6px",
                  }}
                >
                  {bt(
                    "bookingReference",
                    "Booking Reference"
                  )}
                  {" : "}
                  <strong>
                    {fullBookingNo}
                  </strong>
                </p>
              )}

            </div>

            <div
              className="conform1"
              style={{
                width: "80%",
                float: "left",
              }}
            >

              <p>
                {bt(
                  "dear",
                  "Dear"
                )}
                {" "}
                {customer.title}
                {" "}
                {customer.firstName}
                {" "}
                {customer.lastName},
              </p>

              <p>
                {bt(
                  "bookingThankYou",
                  "Thank you for your booking. Your reservation has been successfully entered into our system."
                )}
              </p>

            </div>
{/* 
            <div
              className="conform1"
              style={{
                width: "20%",
                float: "right",
              }}
            >

              <div
                style={{
                  width: "100px",
                  float: "right",
                  textAlign: "center",
                }}
              >

                <div
                  onClick={() =>
                    window.print()
                  }
                  style={{
                    cursor: "pointer",
                  }}
                >
                  <i
                    className="fa-solid fa-print"
                    style={{
                      fontSize: "60px",
                    }}
                  />
                </div>

                <div className="tbltxtnew">
                  {bt(
                    "printPreview",
                    "Print preview"
                  )}
                </div>

              </div>

              <div
                style={{
                  width: "100px",
                  float: "left",
                  textAlign: "center",
                }}
              >

                <div
                  onClick={() =>
                    navigate("/bookacar")
                  }
                  style={{
                    cursor: "pointer",
                  }}
                >
                  <i
                    className="fa-solid fa-plus"
                    style={{
                      fontSize: "60px",
                    }}
                  />
                </div>

                <div
                  className="tbltxtnew"
                  style={{
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    navigate("/bookacar")
                  }
                >
                  {bt(
                    "newReservation",
                    "New reservation"
                  )}
                </div>

              </div>

            </div> */}
<div
  style={{
    width: "100px",
    float: "right",
    textAlign: "center",
  }}
>
  <div
    onClick={() => window.print()}
    style={{
      cursor: "pointer",
    }}
  >
    <img
      src={printImage}
      alt={bt(
        "printPreview",
        "Print preview"
      )}
      title={bt(
        "printPreview",
        "Print preview"
      )}
      width="80"
      height="80"
      style={{
        display: "block",
        margin: "0 auto",
      }}
    />
  </div>

  <div className="tbltxtnew">
    {bt(
      "printPreview",
      "Print preview"
    )}
  </div>
</div>

<div
  style={{
    width: "100px",
    float: "left",
    textAlign: "center",
  }}
>
  <div
    onClick={() => navigate("/bookacar")}
    style={{
      cursor: "pointer",
    }}
  >
    <img
      src={newReservationImage}
      alt={bt(
        "newReservation",
        "New reservation"
      )}
      title={bt(
        "newReservation",
        "New reservation"
      )}
      width="80"
      height="80"
      style={{
        display: "block",
        margin: "0 auto",
      }}
    />
  </div>

  <div
    className="tbltxtnew"
    style={{
      cursor: "pointer",
    }}
    onClick={() => navigate("/bookacar")}
  >
    {bt(
      "newReservation",
      "New reservation"
    )}
  </div>
</div>
            <div className="clearfix" />

          </div>

          {/* ==================================================
              CAR
          ================================================== */}

          <div className="rental-card">

            <div className="card-main-content">

              <div
                className="col-md-9"
                style={{
                  float: "left",
                }}
              >

                <h2 className="car-title">

                  {carName}

                  {" "}

                  <span>
                    (
                    {bt(
                      "orSimilar",
                      "or similar"
                    )}
                    )
                  </span>

                </h2>

                <div className="car-subtitle">
                  {category}
                </div>

              </div>

              <div
                className="col-md-3"
                style={{
                  float: "left",
                }}
              >

                <div className="price-container">

                  <div className="price-label">
                    {bt(
                      "finalPrice",
                      "Final Price"
                    )}
                  </div>

                  <div className="main-price">
                    €{" "}
                    {finalTotal.toFixed(2)}
                  </div>

                </div>

              </div>

              <div className="clearfix" />

              {/* CAR IMAGE */}

              <div
                className="col-md-4"
                style={{
                  float: "left",
                }}
              >

                <div className="image-container">

                  {carImage && (
                    <img
                      src={carImage}
                      alt={carName}
                      className="car-img"
                      onError={(event) => {
                        event.currentTarget.style.display =
                          "none";
                      }}
                    />
                  )}

                </div>

              </div>

              {/* FEATURES */}

              <div
                className="col-md-4"
                style={{
                  float: "left",
                }}
              >

                <div className="features-container">

                  <div className="specs-row">

                    <span className="spec-tag">
                      👤 {passengers}
                    </span>

                    <span className="spec-tag">
                      🧳 {luggage}
                    </span>

                    <span className="spec-tag">
                      ❄️ {airCondition}
                    </span>

                    <span className="spec-tag">
                      ⚙️ {transmission}
                    </span>

                  </div>

                  <div className="feature-item">
                    ✓{" "}
                    <strong>
                      {bt(
                        "noExtraCost",
                        "No Extra Cost"
                      )}
                    </strong>
                  </div>

                  <div className="feature-item">
                    ✓{" "}
                    <strong>
                      {bt(
                        "allInclusive",
                        "All inclusive"
                      )}
                    </strong>
                  </div>

                  <div className="feature-item">
                    ✓{" "}
                    <strong>
                      {bt(
                        "noGuarantee",
                        "No guarantee"
                      )}
                    </strong>
                  </div>

                  <div className="feature-item">
                    ✓{" "}
                    <strong>
                      {bt(
                        "noCreditCardRequired",
                        "No credit card required"
                      )}
                    </strong>
                  </div>

                  <div className="feature-item">
                    ✓{" "}
                    <strong>
                      {bt(
                        "freeRoadmap",
                        "Free roadmap of Crete"
                      )}
                    </strong>
                  </div>

                  <div className="feature-item">
                    ✓{" "}
                    <strong>
                      {bt(
                        "unlimitedMileage",
                        "Unlimited mileage"
                      )}
                    </strong>
                  </div>

                  <div className="feature-item">
                    ✓{" "}
                    <strong>
                      {bt(
                        "roadAssistance",
                        "24/7 road assistance"
                      )}
                    </strong>
                  </div>

                  <div className="feature-item">
                    ✓{" "}
                    <strong>
                      {bt(
                        "fullInsurance",
                        "Full insurance"
                      )}
                    </strong>
                  </div>

                </div>

              </div>

              {/* NOTE */}

              <div
                className="col-md-4"
                style={{
                  float: "left",
                }}
              >

                <div className="actions-container">

                  <div className="car-d notice">
                    {bt(
                      "minimumDriverAgeNote",
                      "Note: The minimum driver age for this car is 21."
                    )}
                  </div>

                </div>

              </div>

              <div className="clearfix" />

            </div>

          </div>

          {/* ==================================================
              ITINERARY
          ================================================== */}

          <div className="l-box">

            <div className="extra-details">

              {bt(
                "yourItinerary",
                "Your itinerary."
              )}

              {" "}

              {bt(
                "totalDaysOfHire",
                "Total days of hire"
              )}

              {" "}

              {rentalDays}

              {" "}

              {bt(
                "days",
                "day(s)"
              )}

            </div>

            <div className="car-box price-box">

              <table
                className="table word-break"
                width="100%"
              >

                <tbody>

                  <tr>

                    <td className="bookingtbltxt">
                      <strong>
                        {bt(
                          "pickUpLocation",
                          "Pick up location"
                        )}
                        {" : "}
                      </strong>

                      {getLocationName(
                        bookingCriteria?.cmbPickLocation
                      )}

                    </td>

                    <td className="bookingtbltxt">
                      <strong>
                        {bt(
                          "pickUpDate",
                          "Pick up date"
                        )}
                        {" : "}
                      </strong>

                      {bookingCriteria?.pickdate}

                    </td>

                    <td className="bookingtbltxt">
                      <strong>
                        {bt(
                          "pickUpTime",
                          "Pick up time"
                        )}
                        {" : "}
                      </strong>

                      {getTime(
                        bookingCriteria?.cmbpickHour,
                        bookingCriteria?.cmbpickmin
                      )}

                    </td>

                  </tr>

                  <tr>

                    <td className="bookingtbltxt">
                      <strong>
                        {bt(
                          "returnLocation",
                          "Return location"
                        )}
                        {" : "}
                      </strong>

                      {getLocationName(
                        bookingCriteria?.cmbDropLocation
                      )}

                    </td>

                    <td className="bookingtbltxt">
                      <strong>
                        {bt(
                          "returnDate",
                          "Return date"
                        )}
                        {" : "}
                      </strong>

                      {bookingCriteria?.returndate}

                    </td>

                    <td className="bookingtbltxt">
                      <strong>
                        {bt(
                          "returnTime",
                          "Return time"
                        )}
                        {" : "}
                      </strong>

                      {getTime(
                        bookingCriteria?.cmbreturnHour,
                        bookingCriteria?.cmbreturnmin
                      )}

                    </td>

                  </tr>

                </tbody>

              </table>

            </div>

          </div>

          {/* ==================================================
              PRICE BREAKDOWN
          ================================================== */}

          <div className="rate">

            {rt(
              "ratechart4",
              "Rate charts"
            )}

            :

          </div>

          <div className="price-box">

            <table
              className="table word-break"
              width="100%"
            >

              <tbody>

                <tr>

                  <td>
                    {rt(
                      "ratechart0",
                      "Quote"
                    )}
                  </td>

                  <td align="center">
                    {rt(
                      "ratechart1",
                      "Calculation"
                    )}
                  </td>

                  <td align="right">
                    {rt(
                      "ratechart2",
                      "Total"
                    )}
                  </td>

                </tr>

                <tr>

                  <td>
                    {rt(
                      "ratechart5",
                      "Rental price"
                    )}
                  </td>

                  <td align="center">
                    {rentalDays}
                    {" × "}
                    {pricePerDay.toFixed(2)}
                    {" €"}
                  </td>

                  <td align="right">
                    {rentalPrice.toFixed(2)}
                    {" €"}
                  </td>

                </tr>

                <tr>

                  <td colSpan="2">
                    {rt(
                      "ratechart6",
                      "Pick up charge"
                    )}
                  </td>

                  <td align="right">
                    {pickupCharge.toFixed(2)}
                    {" €"}
                  </td>

                </tr>

                <tr>

                  <td colSpan="2">
                    {rt(
                      "ratechart3",
                      "Drop off charge"
                    )}
                  </td>

                  <td align="right">
                    {dropoffCharge.toFixed(2)}
                    {" €"}
                  </td>

                </tr>

                <tr>

                  <td colSpan="2">
                    {rt(
                      "ratechart19",
                      "One way rental charges"
                    )}
                  </td>

                  <td align="right">
                    {oneWayCharge.toFixed(2)}
                    {" €"}
                  </td>

                </tr>

                <tr>

                  <td colSpan="2">
                    <strong>
                      {rt(
                        "ratechart10",
                        "Total rental price"
                      )}
                    </strong>
                  </td>

                  <td align="right">
                    <strong>
                      {rentalTotal.toFixed(2)}
                      {" €"}
                    </strong>
                  </td>

                </tr>

                <tr>

                  <td colSpan="2">
                    <strong>
                      {rt(
                        "ratechart13",
                        "Total extra charges"
                      )}
                    </strong>
                  </td>

                  <td align="right">
                    <strong>
                      {paidExtrasTotal.toFixed(2)}
                      {" €"}
                    </strong>
                  </td>

                </tr>

                <tr
                  style={{
                    background: "#edf7ec",
                  }}
                >

                  <td colSpan="2">
                    <strong>
                      {rt(
                        "ratechart14",
                        "Final total rental price"
                      )}
                    </strong>
                  </td>

                  <td align="right">
                    <strong>
                      {finalTotal.toFixed(2)}
                      {" €"}
                    </strong>
                  </td>

                </tr>

              </tbody>

            </table>

          </div>

          {/* ==================================================
              EXTRA DETAILS
          ================================================== */}

          <div className="extra-details">

            {et(
              "extracharges0",
              "Extra details"
            )}

            :

          </div>

          <div className="car-box price-box">

            <table
              className="table word-break"
              width="100%"
            >

              <thead>

                <tr>

                  <td>
                    {et(
                      "extracharges4",
                      "Items"
                    )}
                  </td>

                  <td>
                    {et(
                      "extracharges5",
                      "Rate per day"
                    )}
                  </td>

                  <td>
                    {et(
                      "extracharges6",
                      "Total"
                    )}
                  </td>

                </tr>

              </thead>

              <tbody>

                {activePaidExtras.length >
                0 ? (

                  activePaidExtras.map(
                    (item) => (
                      <tr
                        key={item.id}
                      >

                        <td>

                          <strong>
                            {item.name ||
                              item.optitem_name ||
                              item.optdet_name ||
                              ""}
                          </strong>

                          {(item.description ||
                            item.optitem_description ||
                            item.optdet_description) && (
                            <>
                              {" "}
                              {item.description ||
                                item.optitem_description ||
                                item.optdet_description}
                            </>
                          )}

                        </td>

                        <td>

                          {Number(
                            item.dayRate || 0
                          ).toFixed(2)}

                          {" €/ "}

                          {et(
                            "extracharges10",
                            "Day"
                          )}

                        </td>

                        <td>

                          <strong>
                            {Number(
                              item.total || 0
                            ).toFixed(2)}

                            {" €"}
                          </strong>

                        </td>

                      </tr>
                    )
                  )

                ) : (

                  <tr>

                    <td colSpan="3">

                      {paidExtrasTotal >
                      0
                        ? `${et(
                            "extracharges6",
                            "Total"
                          )}: ${paidExtrasTotal.toFixed(
                            2
                          )} €`
                        : et(
                            "noOptionalExtrasSelected",
                            "No optional extras selected."
                          )}

                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

          {/* ==================================================
              FREE EXTRAS
          ================================================== */}

          {activeFreeExtras.length >
            0 && (
            <>
              <div className="extra-details">

                {et(
                  "extracharges1",
                  "Free optional extras"
                )}

              </div>

              <div className="car-box price-box">

                <table
                  className="table word-break"
                  width="100%"
                >

                  <thead>

                    <tr>

                      <td>
                        {et(
                          "extracharges4",
                          "Items"
                        )}
                      </td>

                    </tr>

                  </thead>

                  <tbody>

                    {activeFreeExtras.map(
                      (item) => (
                        <tr
                          key={
                            item.optdet_id ??
                            item.optitem_id
                          }
                        >

                          <td>

                            {item.name ||
                              item.optitem_name ||
                              item.optdet_name ||
                              ""}

                          </td>

                        </tr>
                      )
                    )}

                  </tbody>

                </table>

              </div>
            </>
          )}

          {/* ==================================================
              INSURANCE
          ================================================== */}

          <div className="extra-details">

            {et(
              "extracharges7",
              "Insurances"
            )}

          </div>

          <div className="car-box price-box">

            <div className="bookingtbltxt">

              <p>
                {et(
                  "alsoIncludedTaxesMileage",
                  "Also included: all taxes, unlimited mileage."
                )}
              </p>

              <p>
                {et(
                  "cdw",
                  "CDW (Collision Damage Waiver)"
                )}
              </p>

              <p>
                {et(
                  "fdw",
                  "FDW (Full Damage Waiver)"
                )}
              </p>

              <p>
                {et(
                  "pai",
                  "PAI (Personal Accident Insurance)"
                )}
              </p>

              <p>
                {et(
                  "tw",
                  "TW (Theft and Fire Insurance)"
                )}
              </p>

              <p>
                {et(
                  "wug",
                  "WUG (Coverage Wheels, Underneath car and Glass)"
                )}
              </p>

              <p>
                {et(
                  "tpi",
                  "TPI (Third Party Insurance)"
                )}
              </p>

            </div>

          </div>

          {/* ==================================================
              PAID INSURANCE
          ================================================== */}

          {activePaidInsurance.length >
            0 && (
            <div className="car-box price-box">

              <table
                className="table word-break"
                width="100%"
              >

                <thead>

                  <tr>

                    <td>
                      {et(
                        "extracharges4",
                        "Items"
                      )}
                    </td>

                    <td>
                      {et(
                        "extracharges5",
                        "Rate per day"
                      )}
                    </td>

                    <td>
                      {et(
                        "extracharges6",
                        "Total"
                      )}
                    </td>

                  </tr>

                </thead>

                <tbody>

                  {activePaidInsurance.map(
                    (item) => (
                      <tr
                        key={item.id}
                      >

                        <td>

                          {item.name ||
                            item.insitem_name ||
                            item.insdtl_name ||
                            ""}

                        </td>

                        <td>

                          {Number(
                            item.ratePerDay ||
                              0
                          ).toFixed(2)}

                          {" €/ "}

                          {et(
                            "extracharges10",
                            "Day"
                          )}

                        </td>

                        <td>

                          <strong>
                            {Number(
                              item.total || 0
                            ).toFixed(2)}
                            {" €"}
                          </strong>

                        </td>

                      </tr>
                    )
                  )}

                </tbody>

              </table>

            </div>
          )}

          {/* ==================================================
              FREE INSURANCE
          ================================================== */}

          {activeFreeInsurance.length >
            0 && (
            <div className="car-box price-box">

             
            </div>
          )}

          {/* ==================================================
              PERSONAL DETAILS
          ================================================== */}

          <div className="extra-details">

            {pt(
              "personaldtls0",
              "Personal details"
            )}

            :

          </div>

          <div className="car-box price-box">

            <table
              className="table word-break"
              width="100%"
            >

              <tbody>

                <tr>

                  <td>

                    <strong>
                      {pt(
                        "personaldtls2",
                        "Name"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {customer.title}{" "}
                    {customer.firstName}

                  </td>

                  <td>

                    <strong>
                      {pt(
                        "personaldtls3",
                        "Family name"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {customer.lastName}

                  </td>

                  <td>

                    <strong>
                      {pt(
                        "personaldtls8",
                        "Mobile no"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {customer.phone}

                  </td>

                  <td>

                    <strong>
                      {pt(
                        "personaldtls4",
                        "Email ID"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {customer.email}

                  </td>

                </tr>

                <tr>

                  <td>

                    <strong>
                      {pt(
                        "personaldtls14",
                        "Home Address"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {customer.address}

                  </td>

                  <td>

                    <strong>
                      {pt(
                        "personaldtls9",
                        "Pick up location"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {getLocationName(
                      bookingCriteria?.cmbPickLocation
                    )}

                  </td>

                  <td>

                    <strong>
                      {pt(
                        "personaldtls16",
                        "Zip code"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {customer.postcode}

                  </td>

                  <td>

                    <strong>
                      {pt(
                        "personaldtls17",
                        "Drop off location"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {getLocationName(
                      bookingCriteria?.cmbDropLocation
                    )}

                  </td>

                </tr>

                <tr>

                  <td>

                    <strong>
                      {pt(
                        "personaldtls15",
                        "City"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {customer.city}

                  </td>

                  <td>

                    <strong>
                      {pt(
                        "personaldtls10",
                        "Country"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {customer.country}

                  </td>

                  <td colSpan="2">

                    <strong>
                      {pt(
                        "personaldtls23",
                        "Pick up comments"
                      )}
                      {" : "}
                    </strong>

                    <br />

                    {customer.remarks}

                  </td>

                </tr>

              </tbody>

            </table>

          </div>

          {/* ==================================================
              PAYMENT
          ================================================== */}

          <div className="extra-details">

            {bt(
              "payment",
              "Payment"
            )}

            :

          </div>

          <div className="car-box price-box">

            <p>

              <strong>
                {bt(
                  "paymentMethod",
                  "Payment method"
                )}
                {" : "}
              </strong>

              {booking.paymentMethod ===
              "paynow"
                ? bt(
                    "payNow",
                    "Pay now"
                  )
                : bt(
                    "payLater",
                    "Pay later"
                  )}

            </p>

            <p>

              <strong>
                {rt(
                  "ratechart14",
                  "Final total"
                )}
                {" : "}
              </strong>

              €{" "}
              {finalTotal.toFixed(2)}

            </p>

          </div>

        </div>

        <div className="clearfix" />

      </div>

    </div>
  );
}

export default ConfirmBooking;